/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import * as React from "react";
import { useDashboardKpiWidgetsQuery } from "~/generated/graphql";
import { useCurrency } from "~/hooks";
import { formatMoney } from "~/tools";

type Data = ReturnType<typeof useDashboardKpiWidgetsQuery>["data"];

export function useWidgetData() {
  const { data, loading, error } = useDashboardKpiWidgetsQuery();
  const { currency } = useCurrency();

  const { costAllocationCoverage, overallWaste, nonProdHours } = React.useMemo(
    () => mapData(data, currency),
    [currency, data]
  );

  return {
    loading,
    error,
    costAllocationCoverage,
    overallWaste,
    nonProdHours,
    businessContext: {
      id: data?.primaryBusinessContext?.id,
      name: data?.primaryBusinessContext?.name,
    },
  };
}

function mapData(data: Data, currency: string) {
  const { costAllocationCoverage, overallWaste, nonProdHours } =
    data?.primaryBusinessContext?.kpis ?? {};

  const allocatedCost = data?.primaryBusinessContext?.costAllocation?.allocated;

  return {
    costAllocationCoverage: {
      value: costAllocationCoverage?.details?.metrics?.currentValue,
      target: costAllocationCoverage?.details?.targets?.currentTarget?.goal,
      additionalValue: formatMoney(allocatedCost, currency),
    },
    overallWaste: {
      value: overallWaste?.details?.metrics?.currentValue,
      target: overallWaste?.details?.targets?.currentTarget?.goal,
      additionalValue: formatMoney(overallWaste?.totalWaste, currency),
    },
    nonProdHours: {
      value: nonProdHours?.details?.metrics?.currentValue,
      target: nonProdHours?.details?.targets?.currentTarget?.goal,
      additionalValue: nonProdHours?.totalHours
        ? `${nonProdHours?.totalHours} h`
        : "",
    },
  };
}
